/**
 * @fileoverview Main Split Screen 2P Live Game Stage Component for "Esquivador" (Surfer - Dynamic Id: 51).
 * Features dual independent native 2D HTML5 Canvases running at 60 FPS via requestAnimationFrame,
 * non-linear 2.5D perspective projection ("Whoosh Curve"), lateral banking tilt on lane switching,
 * responsive particle engines, floating popups, collision detection with invulnerability blinking,
 * authoritative telemetry synchronization, and victory podium overlay.
 * 
 * @module components/Screen/Sections/Surfer/SurferGame
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { GetConfig, ReportHit, UpdateScore } from '../../../../utilities/BL/Surfer';
import {
    resolveAssetUrl,
    preloadImageUrls,
    DEFAULT_BACKGROUND_SVG,
    DEFAULT_ROAD_SVG,
    DEFAULT_PLAYER1_SURFER_SVG,
    DEFAULT_PLAYER2_SURFER_SVG,
    DEFAULT_OBSTACLE_SVG,
    DEFAULT_COIN_SVG,
    DEFAULT_BONUS_SVG,
    DEFAULT_COCKPIT_SVG,
    DEFAULT_TROPHY_SVG
} from './SurferAssets';

/**
 * Default game physics and configuration parameters.
 * @constant {Object}
 */
const DEFAULT_GAME_CONFIG = {
    laneCount: 3,
    baseSpeed: 380,
    maxSpeed: 650,
    speedAcceleration: 5.0,
    densityFactor: 1.0,
    pointsPerTick: 5,
    pointsPerCoin: 50,
    pointsPerBonus: 500,
    penaltyPerHit: 150,
    winMode: 'points_deduction', // 'points_deduction' | 'sudden_death'
    endCondition: 'time',
    timeLimitSeconds: 45,
    invulnerabilityDuration: 1.5,
    primaryColor: '#00ffcc',
    secondaryColor: '#ff007f'
};

/**
 * SurferGame Component (/screen - Component 51.1)
 * 
 * @function SurferGame
 * @param {Object} props - Component properties.
 * @param {Object} [props.currentMatchState] - Live telemetry and match state passed by Screen.js.
 * @param {Object} [props.theme] - Theme object passed from Screen.js or socket payload.
 * @param {Object} [props.payload] - Raw socket packet fallback.
 * @param {Object} [props.config] - Technical configuration parameters.
 * @param {string} [props.roomId] - Room GUID.
 * @returns {React.ReactElement} The rendered split-screen stadium game.
 */
export const SurferGame = ({
    currentMatchState: propMatchState,
    theme: propTheme,
    payload,
    config: propConfig,
    roomId
}) => {
    // =========================================================================
    // 1. CONSOLIDACIÓN DE ESTADO Y TELEMETRÍA AUTORITATIVA
    // =========================================================================
    const canvasP1Ref = useRef(null);
    const canvasP2Ref = useRef(null);
    const containerRef = useRef(null);

    // Referencias a imágenes cargadas en memoria para renderizado directo en Canvas
    const imageCacheRef = useRef({
        bgImg: null,
        roadImg: null,
        p1Img: null,
        p2Img: null,
        obsImg: null,
        coinImg: null,
        bonusImg: null,
        cockpitImg: null,
        trophyImg: null,
        loaded: false
    });

    // Combinar fuentes de estado
    const matchState = useMemo(() => {
        const raw = propMatchState || payload?.matchState || payload?.state || payload || {};
        return {
            phase: raw.phase || 'game_ready', // 'idle' | 'game_ready' | 'countdown' | 'running' | 'paused' | 'finished'
            countdownSeconds: raw.countdownSeconds ?? raw.countdownNumber ?? 0,
            secondsRemaining: raw.secondsRemaining ?? raw.remainingSeconds ?? 45,
            durationSeconds: raw.durationSeconds ?? raw.roundDuration ?? 45,
            currentSpeed: raw.currentSpeed ?? 380,
            trackDistanceTraveled: raw.trackDistanceTraveled ?? 0,
            winnerSide: raw.winnerSide || null,
            winnerName: raw.winnerName || '',
            winReason: raw.winReason || '',
            player1: {
                side: 'player1',
                name: raw.player1?.name || 'Jugador 1',
                score: raw.player1?.score ?? 0,
                hits: raw.player1?.hits ?? 0,
                coinsCollected: raw.player1?.coinsCollected ?? 0,
                bonusesCollected: raw.player1?.bonusesCollected ?? 0,
                currentLane: raw.player1?.currentLane ?? 1,
                isInvulnerable: Boolean(raw.player1?.isInvulnerable),
                isJumping: Boolean(raw.player1?.isJumping),
                isSliding: Boolean(raw.player1?.isSliding),
                state: raw.player1?.state || 'alive',
                avatarUrl: raw.player1?.avatarUrl || null
            },
            player2: {
                side: 'player2',
                name: raw.player2?.name || 'Jugador 2',
                score: raw.player2?.score ?? 0,
                hits: raw.player2?.hits ?? 0,
                coinsCollected: raw.player2?.coinsCollected ?? 0,
                bonusesCollected: raw.player2?.bonusesCollected ?? 0,
                currentLane: raw.player2?.currentLane ?? 1,
                isInvulnerable: Boolean(raw.player2?.isInvulnerable),
                isJumping: Boolean(raw.player2?.isJumping),
                isSliding: Boolean(raw.player2?.isSliding),
                state: raw.player2?.state || 'alive',
                avatarUrl: raw.player2?.avatarUrl || null
            },
            theme: raw.theme || propTheme?.theme || propTheme || {},
            config: raw.config || propConfig || {}
        };
    }, [propMatchState, payload, propTheme, propConfig]);

    // Tema consolidado
    const activeTheme = useMemo(() => {
        return {
            ...DEFAULT_GAME_CONFIG,
            ...(matchState.theme || {}),
            ...(propTheme || {})
        };
    }, [matchState.theme, propTheme]);

    // Configuración consolidada
    const gameConfig = useMemo(() => {
        return {
            ...DEFAULT_GAME_CONFIG,
            ...(matchState.config || {}),
            ...(propConfig || {})
        };
    }, [matchState.config, propConfig]);

    // =========================================================================
    // 2. ESTADO DE SIMULACIÓN Y FISICA 2.5D (EN MEMORIA DE ALTO RENDIMIENTO)
    // =========================================================================
    const simRef = useRef({
        lastTime: performance.now(),
        speed: gameConfig.baseSpeed,
        distance: 0,
        laneCount: gameConfig.laneCount || 3,
        spawnTimer: 0,
        nextSpawnDelay: 1.2,
        // Entidades compartidas en pista (sincronizadas entre ambos bandos)
        entities: [], // { id, type: 'obstacle'|'coin'|'bonus', lane: 0..2, z: 1.0, collectedP1: false, collectedP2: false, hitP1: false, hitP2: false }
        // Telemetría local suavizada e interpolada
        p1: {
            lane: 1,
            targetLane: 1,
            visualX: 0.5,
            lastVisualX: 0.5,
            tilt: 0, // Inclinación en radianes eje Z (banking)
            score: 0,
            hits: 0,
            coins: 0,
            bonuses: 0,
            invulnerableUntil: 0,
            shake: 0,
            state: 'alive',
            jumpProgress: 0,
            slideProgress: 0,
            particles: [],
            floatingTexts: []
        },
        p2: {
            lane: 1,
            targetLane: 1,
            visualX: 0.5,
            lastVisualX: 0.5,
            tilt: 0,
            score: 0,
            hits: 0,
            coins: 0,
            bonuses: 0,
            invulnerableUntil: 0,
            shake: 0,
            state: 'alive',
            jumpProgress: 0,
            slideProgress: 0,
            particles: [],
            floatingTexts: []
        }
    });

    // Sincronizar cambios de carril y datos autoritativos que vienen del WebSocket
    useEffect(() => {
        const sim = simRef.current;
        if (matchState.player1) {
            sim.p1.targetLane = Math.max(0, Math.min(sim.laneCount - 1, matchState.player1.currentLane));
            sim.p1.score = matchState.player1.score;
            sim.p1.hits = matchState.player1.hits;
            sim.p1.coins = matchState.player1.coinsCollected;
            sim.p1.bonuses = matchState.player1.bonusesCollected;
            if (matchState.player1.state) sim.p1.state = matchState.player1.state;
            if (matchState.player1.isInvulnerable && sim.p1.invulnerableUntil < performance.now()) {
                sim.p1.invulnerableUntil = performance.now() + (gameConfig.invulnerabilityDuration * 1000);
            }
        }
        if (matchState.player2) {
            sim.p2.targetLane = Math.max(0, Math.min(sim.laneCount - 1, matchState.player2.currentLane));
            sim.p2.score = matchState.player2.score;
            sim.p2.hits = matchState.player2.hits;
            sim.p2.coins = matchState.player2.coinsCollected;
            sim.p2.bonuses = matchState.player2.bonusesCollected;
            if (matchState.player2.state) sim.p2.state = matchState.player2.state;
            if (matchState.player2.isInvulnerable && sim.p2.invulnerableUntil < performance.now()) {
                sim.p2.invulnerableUntil = performance.now() + (gameConfig.invulnerabilityDuration * 1000);
            }
        }
        if (matchState.currentSpeed) {
            sim.speed = matchState.currentSpeed;
        }
    }, [matchState, gameConfig]);

    // =========================================================================
    // 3. CARGA Y CACHÉ DE RECURSOS GRÁFICOS PARA CANVAS 2D
    // =========================================================================
    useEffect(() => {
        let isMounted = true;

        const loadAssets = async () => {
            const urlsToLoad = [
                { key: 'bgImg', url: resolveAssetUrl(activeTheme.backgroundUrl, DEFAULT_BACKGROUND_SVG) },
                { key: 'roadImg', url: resolveAssetUrl(activeTheme.roadUrl, DEFAULT_ROAD_SVG) },
                { key: 'p1Img', url: resolveAssetUrl(activeTheme.player1Url, DEFAULT_PLAYER1_SURFER_SVG) },
                { key: 'p2Img', url: resolveAssetUrl(activeTheme.player2Url, DEFAULT_PLAYER2_SURFER_SVG) },
                { key: 'obsImg', url: resolveAssetUrl(activeTheme.obstacleUrl, DEFAULT_OBSTACLE_SVG) },
                { key: 'coinImg', url: resolveAssetUrl(activeTheme.coinUrl, DEFAULT_COIN_SVG) },
                { key: 'bonusImg', url: resolveAssetUrl(activeTheme.bonusUrl, DEFAULT_BONUS_SVG) },
                { key: 'cockpitImg', url: resolveAssetUrl(activeTheme.cockpitUrl, DEFAULT_COCKPIT_SVG) },
                { key: 'trophyImg', url: DEFAULT_TROPHY_SVG }
            ];

            const results = await preloadImageUrls(urlsToLoad.map(item => item.url));
            if (!isMounted) return;

            urlsToLoad.forEach(item => {
                const found = results.find(r => r.url === item.url);
                if (found && found.image) {
                    imageCacheRef.current[item.key] = found.image;
                } else {
                    const fallbackImg = new Image();
                    fallbackImg.src = item.url;
                    imageCacheRef.current[item.key] = fallbackImg;
                }
            });

            imageCacheRef.current.loaded = true;
        };

        loadAssets();

        return () => {
            isMounted = false;
        };
    }, [activeTheme]);

    // =========================================================================
    // 4. GENERADOR Y PROCESADOR DE ENTIDADES Y COLISIONES
    // =========================================================================
    const spawnEntity = useCallback(() => {
        const sim = simRef.current;
        const rand = Math.random();
        const lane = Math.floor(Math.random() * sim.laneCount);

        let type = 'obstacle';
        if (rand < 0.45) {
            type = 'obstacle';
        } else if (rand < 0.88) {
            type = 'coin';
        } else {
            type = 'bonus';
        }

        const id = `${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;

        if (type === 'coin') {
            // Generar hilera de monedas en el carril
            const coinCount = 3;
            for (let i = 0; i < coinCount; i++) {
                sim.entities.push({
                    id: `${id}_${i}`,
                    type: 'coin',
                    lane,
                    z: 1.0 + (i * 0.08),
                    collectedP1: false,
                    collectedP2: false,
                    hitP1: false,
                    hitP2: false
                });
            }
        } else {
            sim.entities.push({
                id,
                type,
                lane,
                z: 1.0,
                collectedP1: false,
                collectedP2: false,
                hitP1: false,
                hitP2: false
            });
        }
    }, []);

    // Añadir partículas al sistema
    const addParticles = useCallback((playerSim, x, y, count, color, type = 'spark') => {
        for (let i = 0; i < count; i++) {
            const angle = Math.random() * Math.PI * 2;
            const speed = 60 + Math.random() * 220;
            playerSim.particles.push({
                x,
                y,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed - (type === 'coin' ? 80 : 30),
                size: 3 + Math.random() * 7,
                color,
                life: 0,
                maxLife: 0.4 + Math.random() * 0.5,
                type
            });
        }
    }, []);

    // Añadir texto flotante
    const addFloatingText = useCallback((playerSim, x, y, text, color, stroke = '#000000') => {
        playerSim.floatingTexts.push({
            x,
            y,
            text,
            color,
            stroke,
            vy: -110,
            scale: 1.4,
            alpha: 1.0,
            life: 0,
            maxLife: 1.2
        });
    }, []);

    // Manejar colisión de impacto contra obstáculo
    const handleObstacleHit = useCallback((side, playerSim, canvasWidth, canvasHeight) => {
        const now = performance.now();
        if (playerSim.invulnerableUntil > now) return; // Invulnerable

        playerSim.invulnerableUntil = now + (gameConfig.invulnerabilityDuration * 1000);
        playerSim.shake = 18;
        playerSim.hits += 1;

        if (gameConfig.winMode === 'sudden_death') {
            playerSim.state = 'eliminated';
        } else {
            playerSim.score = Math.max(0, playerSim.score - gameConfig.penaltyPerHit);
        }

        const px = canvasWidth * playerSim.visualX;
        const py = canvasHeight * 0.84;

        addParticles(playerSim, px, py, 25, '#ff0033', 'hit');
        addFloatingText(playerSim, px, py - 20, `-${gameConfig.penaltyPerHit} ¡CHOQUE!`, '#ff0055', '#330011');

        // Notificar al backend para vibración háptica del aficionado
        if (roomId) {
            ReportHit(roomId, side).catch(() => {});
            UpdateScore(roomId, side, -gameConfig.penaltyPerHit, 'HIT_OBSTACLE').catch(() => {});
        }
    }, [gameConfig, roomId, addParticles, addFloatingText]);

    // Manejar recolección de moneda
    const handleCoinCollect = useCallback((side, playerSim, canvasWidth, canvasHeight) => {
        playerSim.coins += 1;
        playerSim.score += gameConfig.pointsPerCoin;

        const px = canvasWidth * playerSim.visualX;
        const py = canvasHeight * 0.84;

        addParticles(playerSim, px, py, 14, '#ffe600', 'coin');
        addFloatingText(playerSim, px, py - 15, `+${gameConfig.pointsPerCoin}`, '#ffe600', '#443300');

        if (roomId) {
            UpdateScore(roomId, side, gameConfig.pointsPerCoin, 'COIN_COLLECTED').catch(() => {});
        }
    }, [gameConfig, roomId, addParticles, addFloatingText]);

    // Manejar recolección de bonus especial
    const handleBonusCollect = useCallback((side, playerSim, canvasWidth, canvasHeight) => {
        playerSim.bonuses += 1;
        playerSim.score += gameConfig.pointsPerBonus;

        const px = canvasWidth * playerSim.visualX;
        const py = canvasHeight * 0.84;

        addParticles(playerSim, px, py, 35, '#00ffea', 'bonus');
        addFloatingText(playerSim, px, py - 25, `+${gameConfig.pointsPerBonus} ¡SUPER BONUS!`, '#00ffea', '#003344');

        if (roomId) {
            UpdateScore(roomId, side, gameConfig.pointsPerBonus, 'BONUS_COLLECTED').catch(() => {});
        }
    }, [gameConfig, roomId, addParticles, addFloatingText]);

    // =========================================================================
    // 5. BUCLE PRINCIPAL DE RENDERIZADO 2D A 60 FPS (MOTOR 2.5D WHOOSH CURVE)
    // =========================================================================
    useEffect(() => {
        let animationFrameId;
        const isRunning = matchState.phase === 'running' || matchState.phase === 'countdown';

        const renderHalf = (canvas, playerSim, side, isLeft) => {
            if (!canvas) return;
            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            const width = canvas.clientWidth;
            const height = canvas.clientHeight;
            if (width === 0 || height === 0) return;

            // Ajustar resolución nativa High-DPI
            const dpr = window.devicePixelRatio || 1;
            if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
                canvas.width = width * dpr;
                canvas.height = height * dpr;
            }

            ctx.save();
            ctx.scale(dpr, dpr);

            // Aplicar Screen Shake en caso de colisión reciente
            if (playerSim.shake > 0) {
                const shakeX = (Math.random() - 0.5) * playerSim.shake;
                const shakeY = (Math.random() - 0.5) * playerSim.shake;
                ctx.translate(shakeX, shakeY);
            }

            // -------------------------------------------------------------
            // A. Fondo de Estadio / Skyline
            // -------------------------------------------------------------
            const bgImg = imageCacheRef.current.bgImg;
            if (bgImg && bgImg.complete && bgImg.naturalWidth > 0) {
                // Parallax suave con el carril del jugador
                const bgOffset = (playerSim.visualX - 0.5) * -30;
                ctx.drawImage(bgImg, bgOffset - 40, 0, width + 80, height * 0.55);
            } else {
                const skyGrad = ctx.createLinearGradient(0, 0, 0, height * 0.5);
                skyGrad.addColorStop(0, '#050014');
                skyGrad.addColorStop(1, '#1b003a');
                ctx.fillStyle = skyGrad;
                ctx.fillRect(0, 0, width, height * 0.5);
            }

            // Resplandor del horizonte
            const horizonY = height * 0.28;
            const horizonGlow = ctx.createRadialGradient(width / 2, horizonY, 10, width / 2, horizonY, width * 0.6);
            horizonGlow.addColorStop(0, isLeft ? 'rgba(0, 255, 204, 0.45)' : 'rgba(255, 0, 127, 0.45)');
            horizonGlow.addColorStop(1, 'transparent');
            ctx.fillStyle = horizonGlow;
            ctx.fillRect(0, horizonY - 80, width, 160);

            // -------------------------------------------------------------
            // B. Pista 2.5D con Curva No Lineal (Whoosh Curve)
            // -------------------------------------------------------------
            const bottomY = height * 0.98;
            const horizonHalfWidth = width * 0.12;
            const bottomHalfWidth = width * 0.44;
            const centerX = width / 2;

            // Polígono de la superficie de la pista
            ctx.beginPath();
            ctx.moveTo(centerX - horizonHalfWidth, horizonY);
            ctx.lineTo(centerX + horizonHalfWidth, horizonY);
            ctx.lineTo(centerX + bottomHalfWidth, bottomY);
            ctx.lineTo(centerX - bottomHalfWidth, bottomY);
            ctx.closePath();

            const roadGrad = ctx.createLinearGradient(0, horizonY, 0, bottomY);
            roadGrad.addColorStop(0, '#0e0026');
            roadGrad.addColorStop(0.6, '#18043c');
            roadGrad.addColorStop(1, '#250854');
            ctx.fillStyle = roadGrad;
            ctx.fill();

            // Bordes Neón de la Pista
            ctx.lineWidth = 4;
            ctx.strokeStyle = isLeft ? '#00ffcc' : '#ff007f';
            ctx.shadowColor = isLeft ? '#00ffcc' : '#ff007f';
            ctx.shadowBlur = 15;
            ctx.beginPath();
            ctx.moveTo(centerX - horizonHalfWidth, horizonY);
            ctx.lineTo(centerX - bottomHalfWidth, bottomY);
            ctx.stroke();

            ctx.beginPath();
            ctx.moveTo(centerX + horizonHalfWidth, horizonY);
            ctx.lineTo(centerX + bottomHalfWidth, bottomY);
            ctx.stroke();
            ctx.shadowBlur = 0; // Reset blur

            // Líneas divisorias de carriles con perspectiva (3 carriles)
            const laneCount = simRef.current.laneCount || 3;
            for (let l = 1; l < laneCount; l++) {
                const laneRatio = (l / laneCount) * 2 - 1; // -0.33, +0.33
                const hx = centerX + horizonHalfWidth * laneRatio;
                const bx = centerX + bottomHalfWidth * laneRatio;

                ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
                ctx.lineWidth = 2;
                ctx.setLineDash([18, 14]);
                ctx.lineDashOffset = -(simRef.current.distance * 0.5) % 32;
                ctx.beginPath();
                ctx.moveTo(hx, horizonY);
                ctx.lineTo(bx, bottomY);
                ctx.stroke();
                ctx.setLineDash([]);
            }

            // Franjas transversales de velocidad (Speed Grid lines)
            const speedLinesCount = 8;
            for (let i = 0; i < speedLinesCount; i++) {
                const baseProgress = ((i / speedLinesCount) + (simRef.current.distance * 0.0012)) % 1.0;
                // Aplicar Whoosh Curve al espaciado: progress = depth^2.15
                const whooshProgress = Math.pow(Math.max(0, baseProgress), 2.15);
                const sy = horizonY + (bottomY - horizonY) * whooshProgress;
                const currentHalfWidth = horizonHalfWidth + (bottomHalfWidth - horizonHalfWidth) * whooshProgress;

                ctx.strokeStyle = `rgba(255, 255, 255, ${whooshProgress * 0.25})`;
                ctx.lineWidth = 1 + whooshProgress * 3;
                ctx.beginPath();
                ctx.moveTo(centerX - currentHalfWidth, sy);
                ctx.lineTo(centerX + currentHalfWidth, sy);
                ctx.stroke();
            }

            // -------------------------------------------------------------
            // C. Renderizado de Entidades en Pista (Obstáculos, Monedas, Bonus)
            // -------------------------------------------------------------
            const entities = simRef.current.entities;
            const playerZ = 0.08;

            // Ordenar entidades por profundidad Z descendente (horizonte a cámara)
            const sortedEntities = [...entities].sort((a, b) => b.z - a.z);

            sortedEntities.forEach(ent => {
                if (ent.z > 1.05 || ent.z < -0.04) return;
                // Si la moneda o bonus ya fue recolectada por este jugador, no dibujarla
                if (isLeft && ent.collectedP1) return;
                if (!isLeft && ent.collectedP2) return;

                // Motor Whoosh Curve:
                // depth = 1 - z
                // progress = max(0, depth)^2.15
                // scale = max(0.02, depth)^1.65
                const depth = 1.0 - ent.z;
                const progress = Math.pow(Math.max(0, depth), 2.15);
                const scale = Math.pow(Math.max(0.02, depth), 1.65);

                const ey = horizonY + (bottomY - horizonY) * progress;
                const currentRoadWidth = horizonHalfWidth + (bottomHalfWidth - horizonHalfWidth) * progress;

                // Posición X en base al carril (3 carriles: -0.66, 0.0, +0.66)
                const laneOffset = (ent.lane - (laneCount - 1) / 2) * (1.35 / laneCount);
                const ex = centerX + currentRoadWidth * laneOffset;

                // Tamaños base
                let baseW = 120;
                let baseH = 90;
                let imgToDraw = null;

                if (ent.type === 'obstacle') {
                    baseW = 140;
                    baseH = 95;
                    imgToDraw = imageCacheRef.current.obsImg;
                } else if (ent.type === 'coin') {
                    baseW = 85;
                    baseH = 85;
                    imgToDraw = imageCacheRef.current.coinImg;
                } else if (ent.type === 'bonus') {
                    baseW = 110;
                    baseH = 110;
                    imgToDraw = imageCacheRef.current.bonusImg;
                }

                const drawW = baseW * scale;
                const drawH = baseH * scale;

                if (imgToDraw && imgToDraw.complete && imgToDraw.naturalWidth > 0) {
                    ctx.save();
                    ctx.translate(ex, ey);

                    // Animación de flotación y brillo para monedas y bonus
                    if (ent.type === 'coin' || ent.type === 'bonus') {
                        const floatY = Math.sin(performance.now() * 0.006 + ent.z * 10) * 8 * scale;
                        ctx.translate(0, floatY);
                        ctx.shadowColor = ent.type === 'coin' ? '#ffe600' : '#00ffea';
                        ctx.shadowBlur = 12 * scale;
                    }

                    ctx.drawImage(imgToDraw, -drawW / 2, -drawH, drawW, drawH);
                    ctx.restore();
                }

                // Detección de colisión cuando la entidad pasa por la posición Z del jugador
                if (Math.abs(ent.z - playerZ) < 0.055) {
                    if (ent.lane === playerSim.targetLane) {
                        if (ent.type === 'obstacle') {
                            if (isLeft && !ent.hitP1) {
                                ent.hitP1 = true;
                                handleObstacleHit('player1', playerSim, width, height);
                            } else if (!isLeft && !ent.hitP2) {
                                ent.hitP2 = true;
                                handleObstacleHit('player2', playerSim, width, height);
                            }
                        } else if (ent.type === 'coin') {
                            if (isLeft && !ent.collectedP1) {
                                ent.collectedP1 = true;
                                handleCoinCollect('player1', playerSim, width, height);
                            } else if (!isLeft && !ent.collectedP2) {
                                ent.collectedP2 = true;
                                handleCoinCollect('player2', playerSim, width, height);
                            }
                        } else if (ent.type === 'bonus') {
                            if (isLeft && !ent.collectedP1) {
                                ent.collectedP1 = true;
                                handleBonusCollect('player1', playerSim, width, height);
                            } else if (!isLeft && !ent.collectedP2) {
                                ent.collectedP2 = true;
                                handleBonusCollect('player2', playerSim, width, height);
                            }
                        }
                    }
                }
            });

            // -------------------------------------------------------------
            // D. Renderizado del Jugador / Vehículo Surfer
            // -------------------------------------------------------------
            const playerDepth = 1.0 - playerZ;
            const playerProgress = Math.pow(Math.max(0, playerDepth), 2.15);
            const playerScale = Math.pow(Math.max(0.02, playerDepth), 1.65);
            const playerY = horizonY + (bottomY - horizonY) * playerProgress;
            const currentRoadWidthAtPlayer = horizonHalfWidth + (bottomHalfWidth - horizonHalfWidth) * playerProgress;

            // X objetivo y posición visual suavizada
            const targetLaneOffset = (playerSim.targetLane - (laneCount - 1) / 2) * (1.35 / laneCount);
            const targetPlayerX = centerX + currentRoadWidthAtPlayer * targetLaneOffset;

            // Convertir a ratio visual 0..1
            const currentPxX = centerX + (playerSim.visualX - 0.5) * (currentRoadWidthAtPlayer * 1.8);

            const isInvulnerable = playerSim.invulnerableUntil > performance.now();
            const shouldBlink = isInvulnerable && Math.sin(performance.now() * 0.035) > 0;

            const playerImg = isLeft ? imageCacheRef.current.p1Img : imageCacheRef.current.p2Img;
            const basePlayerW = 200;
            const basePlayerH = 175;
            const pDrawW = basePlayerW * playerScale;
            const pDrawH = basePlayerH * playerScale;

            ctx.save();
            ctx.translate(currentPxX, playerY);

            // Inclinación lateral (Banking Tilt en eje Z hasta ±6.3° / ±0.11 rad)
            ctx.rotate(playerSim.tilt);

            // Efecto parpadeo de invulnerabilidad
            if (shouldBlink) {
                ctx.globalAlpha = 0.35;
            }

            if (playerImg && playerImg.complete && playerImg.naturalWidth > 0) {
                // Sombra de contacto en pista
                ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
                ctx.beginPath();
                ctx.ellipse(0, 5, pDrawW * 0.45, 12 * playerScale, 0, 0, Math.PI * 2);
                ctx.fill();

                // Dibujar vehículo
                ctx.shadowColor = isLeft ? '#00ffcc' : '#ff007f';
                ctx.shadowBlur = 16;
                ctx.drawImage(playerImg, -pDrawW / 2, -pDrawH * 0.9, pDrawW, pDrawH);
                ctx.shadowBlur = 0;
            }

            // Estela de plasma / propulsor continuo
            const thrusterColor = isLeft ? '#00ffcc' : '#ff007f';
            const flameLen = (15 + Math.random() * 20) * playerScale;
            ctx.fillStyle = '#ffffff';
            ctx.beginPath();
            ctx.moveTo(-15 * playerScale, 0);
            ctx.lineTo(0, flameLen);
            ctx.lineTo(15 * playerScale, 0);
            ctx.closePath();
            ctx.fill();

            ctx.fillStyle = thrusterColor;
            ctx.beginPath();
            ctx.moveTo(-25 * playerScale, -2);
            ctx.lineTo(0, flameLen * 1.5);
            ctx.lineTo(25 * playerScale, -2);
            ctx.closePath();
            ctx.fill();

            ctx.restore();

            // -------------------------------------------------------------
            // E. Sistema de Partículas y Textos Flotantes
            // -------------------------------------------------------------
            playerSim.particles.forEach(p => {
                ctx.save();
                const alpha = Math.max(0, 1.0 - (p.life / p.maxLife));
                ctx.globalAlpha = alpha;
                ctx.fillStyle = p.color;
                ctx.shadowColor = p.color;
                ctx.shadowBlur = 8;
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
                ctx.fill();
                ctx.restore();
            });

            playerSim.floatingTexts.forEach(ft => {
                ctx.save();
                const alpha = Math.max(0, 1.0 - (ft.life / ft.maxLife));
                ctx.globalAlpha = alpha;
                ctx.font = `900 ${Math.round(26 * ft.scale)}px "Montserrat", "Impact", sans-serif`;
                ctx.textAlign = 'center';
                ctx.lineWidth = 4;
                ctx.strokeStyle = ft.stroke;
                ctx.strokeText(ft.text, ft.x, ft.y);
                ctx.fillStyle = ft.color;
                ctx.fillText(ft.text, ft.x, ft.y);
                ctx.restore();
            });

            // -------------------------------------------------------------
            // F. Visor / Cockpit Holográfico Opcional
            // -------------------------------------------------------------
            const cockpitImg = imageCacheRef.current.cockpitImg;
            if (cockpitImg && cockpitImg.complete && activeTheme.cockpitUrl) {
                ctx.drawImage(cockpitImg, 0, 0, width, height);
            }

            ctx.restore();
        };

        // Bucle de animación
        const gameLoop = (currentTime) => {
            const sim = simRef.current;
            const dt = Math.min(0.1, (currentTime - sim.lastTime) / 1000);
            sim.lastTime = currentTime;

            if (isRunning) {
                // Aumentar distancia y velocidad
                sim.distance += sim.speed * dt;
                sim.speed = Math.min(gameConfig.maxSpeed, sim.speed + gameConfig.speedAcceleration * dt);

                // Temporizador de generación de entidades
                sim.spawnTimer += dt;
                if (sim.spawnTimer >= sim.nextSpawnDelay) {
                    sim.spawnTimer = 0;
                    sim.nextSpawnDelay = (1.1 + Math.random() * 0.7) / (gameConfig.densityFactor || 1.0);
                    spawnEntity();
                }

                // Desplazar entidades hacia la cámara
                sim.entities.forEach(ent => {
                    ent.z -= (sim.speed / 1100) * dt;
                });

                // Filtrar entidades que ya salieron de la pantalla
                sim.entities = sim.entities.filter(ent => ent.z >= -0.05);

                // Actualizar física del Jugador 1
                const p1 = sim.p1;
                const p1TargetVisualX = p1.targetLane / (sim.laneCount - 1 || 1);
                p1.lastVisualX = p1.visualX;
                p1.visualX += (p1TargetVisualX - p1.visualX) * Math.min(1.0, 13 * dt);
                const p1Vx = (p1.visualX - p1.lastVisualX) / Math.max(0.001, dt);
                // Banking Tilt: ±0.11 rad (±6.3°)
                const p1TargetTilt = Math.max(-0.11, Math.min(0.11, p1Vx * 0.15));
                p1.tilt += (p1TargetTilt - p1.tilt) * Math.min(1.0, 9 * dt);
                if (p1.shake > 0) p1.shake = Math.max(0, p1.shake - 45 * dt);

                // Actualizar física del Jugador 2
                const p2 = sim.p2;
                const p2TargetVisualX = p2.targetLane / (sim.laneCount - 1 || 1);
                p2.lastVisualX = p2.visualX;
                p2.visualX += (p2TargetVisualX - p2.visualX) * Math.min(1.0, 13 * dt);
                const p2Vx = (p2.visualX - p2.lastVisualX) / Math.max(0.001, dt);
                const p2TargetTilt = Math.max(-0.11, Math.min(0.11, p2Vx * 0.15));
                p2.tilt += (p2TargetTilt - p2.tilt) * Math.min(1.0, 9 * dt);
                if (p2.shake > 0) p2.shake = Math.max(0, p2.shake - 45 * dt);

                // Actualizar partículas P1
                p1.particles.forEach(p => {
                    p.x += p.vx * dt;
                    p.y += p.vy * dt;
                    p.life += dt;
                });
                p1.particles = p1.particles.filter(p => p.life < p.maxLife);

                p1.floatingTexts.forEach(ft => {
                    ft.y += ft.vy * dt;
                    ft.life += dt;
                });
                p1.floatingTexts = p1.floatingTexts.filter(ft => ft.life < ft.maxLife);

                // Actualizar partículas P2
                p2.particles.forEach(p => {
                    p.x += p.vx * dt;
                    p.y += p.vy * dt;
                    p.life += dt;
                });
                p2.particles = p2.particles.filter(p => p.life < p.maxLife);

                p2.floatingTexts.forEach(ft => {
                    ft.y += ft.vy * dt;
                    ft.life += dt;
                });
                p2.floatingTexts = p2.floatingTexts.filter(ft => ft.life < ft.maxLife);
            }

            // Renderizar ambas mitades de pantalla
            renderHalf(canvasP1Ref.current, sim.p1, 'player1', true);
            renderHalf(canvasP2Ref.current, sim.p2, 'player2', false);

            animationFrameId = requestAnimationFrame(gameLoop);
        };

        animationFrameId = requestAnimationFrame(gameLoop);

        return () => {
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
            }
        };
    }, [matchState.phase, gameConfig, spawnEntity, handleObstacleHit, handleCoinCollect, handleBonusCollect]);

    // =========================================================================
    // 6. FORMATEO DE TIEMPO Y VALORES HUD
    // =========================================================================
    const formatTime = (totalSeconds) => {
        const secs = Math.max(0, Math.floor(totalSeconds));
        const m = Math.floor(secs / 60);
        const s = secs % 60;
        return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    };

    const isFinished = matchState.phase === 'finished' || (matchState.winnerSide && matchState.winnerSide !== 'none');
    const isCountdown = matchState.phase === 'countdown' || matchState.countdownSeconds > 0;

    const winnerText = useMemo(() => {
        if (!isFinished) return '';
        if (matchState.winnerSide === 'player1') {
            return `¡GANÓ ${matchState.player1?.name || 'JUGADOR 1'}!`;
        }
        if (matchState.winnerSide === 'player2') {
            return `¡GANÓ ${matchState.player2?.name || 'JUGADOR 2'}!`;
        }
        if (matchState.winnerSide === 'tie' || matchState.isTie) {
            return '¡EMPATE ÉPICO!';
        }
        // Fallback comparando puntajes
        const s1 = matchState.player1?.score ?? 0;
        const s2 = matchState.player2?.score ?? 0;
        if (s1 > s2) return `¡GANÓ ${matchState.player1?.name || 'JUGADOR 1'}!`;
        if (s2 > s1) return `¡GANÓ ${matchState.player2?.name || 'JUGADOR 2'}!`;
        return '¡EMPATE ÉPICO!';
    }, [isFinished, matchState]);

    const displaySpeedKmH = Math.round((simRef.current.speed || gameConfig.baseSpeed) * 0.45);

    return (
        <main
            className="surfer-game-stage"
            ref={containerRef}
            role="region"
            aria-label="Escenario Split Screen Esquivador 1 vs 1"
        >
            {/* Cabecera HUD Central Arcade */}
            <header className="surfer-hud">
                {/* Tarjeta Marcador Jugador 1 */}
                <div className="surfer-hud__player surfer-hud__player--p1" style={{ '--hud-color': activeTheme.primaryColor }}>
                    <div className="surfer-hud__avatar-box">
                        <img
                            src={resolveAssetUrl(matchState.player1.avatarUrl || activeTheme.player1Url, DEFAULT_PLAYER1_SURFER_SVG)}
                            alt="Avatar P1"
                            className="surfer-hud__avatar-img"
                        />
                    </div>
                    <div className="surfer-hud__player-meta">
                        <span className="surfer-hud__player-name">{matchState.player1.name || 'JUGADOR 1'}</span>
                        <div className="surfer-hud__stats-row">
                            <span className="surfer-hud__stat-item">
                                <i className="bi bi-coin" /> {matchState.player1.coinsCollected}
                            </span>
                            <span className="surfer-hud__stat-item surfer-hud__stat-item--hits">
                                <i className="bi bi-shield-slash-fill" /> {matchState.player1.hits}
                            </span>
                        </div>
                    </div>
                    <div className="surfer-hud__score-display">
                        <span className="surfer-hud__score-value">{matchState.player1.score}</span>
                        <span className="surfer-hud__score-label">PTS</span>
                    </div>
                </div>

                {/* Centro: Reloj y Velocímetro Digital */}
                <div className="surfer-hud__center">
                    <div className={`surfer-hud__timer-box ${matchState.secondsRemaining <= 10 ? 'surfer-hud__timer-box--pulse' : ''}`}>
                        <span className="surfer-hud__timer-label">TIEMPO</span>
                        <span className="surfer-hud__timer-value">{formatTime(matchState.secondsRemaining)}</span>
                    </div>
                    <div className="surfer-hud__speed-badge">
                        <i className="bi bi-speedometer2" />
                        <span className="surfer-hud__speed-value">{displaySpeedKmH}</span>
                        <span className="surfer-hud__speed-unit">KM/H</span>
                    </div>
                </div>

                {/* Tarjeta Marcador Jugador 2 */}
                <div className="surfer-hud__player surfer-hud__player--p2" style={{ '--hud-color': activeTheme.secondaryColor }}>
                    <div className="surfer-hud__score-display">
                        <span className="surfer-hud__score-value">{matchState.player2.score}</span>
                        <span className="surfer-hud__score-label">PTS</span>
                    </div>
                    <div className="surfer-hud__player-meta surfer-hud__player-meta--right">
                        <span className="surfer-hud__player-name">{matchState.player2.name || 'JUGADOR 2'}</span>
                        <div className="surfer-hud__stats-row">
                            <span className="surfer-hud__stat-item">
                                <i className="bi bi-coin" /> {matchState.player2.coinsCollected}
                            </span>
                            <span className="surfer-hud__stat-item surfer-hud__stat-item--hits">
                                <i className="bi bi-shield-slash-fill" /> {matchState.player2.hits}
                            </span>
                        </div>
                    </div>
                    <div className="surfer-hud__avatar-box">
                        <img
                            src={resolveAssetUrl(matchState.player2.avatarUrl || activeTheme.player2Url, DEFAULT_PLAYER2_SURFER_SVG)}
                            alt="Avatar P2"
                            className="surfer-hud__avatar-img"
                        />
                    </div>
                </div>
            </header>

            {/* Split Screen 50% / 50% */}
            <div className="surfer-game-stage__split">
                {/* Mitad Izquierda: Jugador 1 */}
                <section className="surfer-game-stage__player-pane surfer-game-stage__player-pane--p1">
                    <canvas
                        ref={canvasP1Ref}
                        className="surfer-game-stage__canvas"
                    />
                    <div className="surfer-game-stage__lane-indicators">
                        <span className={`surfer-game-stage__lane-dot ${matchState.player1.currentLane === 0 ? 'active' : ''}`} />
                        <span className={`surfer-game-stage__lane-dot ${matchState.player1.currentLane === 1 ? 'active' : ''}`} />
                        <span className={`surfer-game-stage__lane-dot ${matchState.player1.currentLane === 2 ? 'active' : ''}`} />
                    </div>
                </section>

                {/* Divisor Central Neón */}
                <div className="surfer-game-stage__divider">
                    <div className="surfer-game-stage__divider-line" />
                    <div className="surfer-game-stage__divider-glow" />
                </div>

                {/* Mitad Derecha: Jugador 2 */}
                <section className="surfer-game-stage__player-pane surfer-game-stage__player-pane--p2">
                    <canvas
                        ref={canvasP2Ref}
                        className="surfer-game-stage__canvas"
                    />
                    <div className="surfer-game-stage__lane-indicators">
                        <span className={`surfer-game-stage__lane-dot ${matchState.player2.currentLane === 0 ? 'active' : ''}`} />
                        <span className={`surfer-game-stage__lane-dot ${matchState.player2.currentLane === 1 ? 'active' : ''}`} />
                        <span className={`surfer-game-stage__lane-dot ${matchState.player2.currentLane === 2 ? 'active' : ''}`} />
                    </div>
                </section>
            </div>

            {/* Overlay de Conteo Regresivo (3, 2, 1, ¡GO!) */}
            {isCountdown && (
                <div className="surfer-countdown-overlay">
                    <div className="surfer-countdown-overlay__content">
                        <span className="surfer-countdown-overlay__number">
                            {matchState.countdownSeconds > 0 ? matchState.countdownSeconds : '¡GO!'}
                        </span>
                        <span className="surfer-countdown-overlay__subtitle">¡PREPÁRATE PARA SURFEAR!</span>
                    </div>
                </div>
            )}

            {/* Modal de Podio de Victoria (Finished) */}
            {isFinished && (
                <div className="surfer-podium-overlay">
                    <div className="surfer-podium-card">
                        {/* Trofeo de Oro Animado */}
                        <div className="surfer-podium-trophy">
                            <img
                                src={DEFAULT_TROPHY_SVG}
                                alt="Trofeo de Victoria"
                                className="surfer-podium-trophy__img"
                            />
                            <div className="surfer-podium-trophy__shine" />
                        </div>

                        {/* Título de Victoria */}
                        <h2 className="surfer-podium-card__title">{winnerText}</h2>
                        {matchState.winReason && (
                            <span className="surfer-podium-card__reason-badge">
                                {matchState.winReason.toUpperCase()}
                            </span>
                        )}

                        {/* Desglose Comparativo de Estadísticas */}
                        <div className="surfer-podium-stats-grid">
                            {/* Stats Jugador 1 */}
                            <div className="surfer-podium-stats-card surfer-podium-stats-card--p1" style={{ '--stat-color': activeTheme.primaryColor }}>
                                <h3 className="surfer-podium-stats-card__name">{matchState.player1.name || 'JUGADOR 1'}</h3>
                                <div className="surfer-podium-stats-card__score">{matchState.player1.score} PTS</div>
                                <ul className="surfer-podium-stats-card__list">
                                    <li><span>Monedas:</span> <strong>{matchState.player1.coinsCollected}</strong></li>
                                    <li><span>Bonuses:</span> <strong>{matchState.player1.bonusesCollected}</strong></li>
                                    <li><span>Choques:</span> <strong>{matchState.player1.hits}</strong></li>
                                </ul>
                            </div>

                            {/* Versus Badge Central */}
                            <div className="surfer-podium-stats-divider">
                                <span>VS</span>
                            </div>

                            {/* Stats Jugador 2 */}
                            <div className="surfer-podium-stats-card surfer-podium-stats-card--p2" style={{ '--stat-color': activeTheme.secondaryColor }}>
                                <h3 className="surfer-podium-stats-card__name">{matchState.player2.name || 'JUGADOR 2'}</h3>
                                <div className="surfer-podium-stats-card__score">{matchState.player2.score} PTS</div>
                                <ul className="surfer-podium-stats-card__list">
                                    <li><span>Monedas:</span> <strong>{matchState.player2.coinsCollected}</strong></li>
                                    <li><span>Bonuses:</span> <strong>{matchState.player2.bonusesCollected}</strong></li>
                                    <li><span>Choques:</span> <strong>{matchState.player2.hits}</strong></li>
                                </ul>
                            </div>
                        </div>

                        {/* Mensaje de Próxima Ronda */}
                        <p className="surfer-podium-card__footer-msg">
                            ESPERANDO AL OPERADOR PARA SIGUIENTE DESAFÍO...
                        </p>
                    </div>
                </div>
            )}
        </main>
    );
};

SurferGame.propTypes = {
    currentMatchState: PropTypes.object,
    theme: PropTypes.object,
    payload: PropTypes.object,
    config: PropTypes.object,
    roomId: PropTypes.string
};

SurferGame.defaultProps = {
    currentMatchState: null,
    theme: null,
    payload: null,
    config: null,
    roomId: null
};

export default SurferGame;
